import React, { useState, useRef, useEffect } from 'react';
import { MessageCircle, X, Send, Bot, User } from 'lucide-react';
import api from '../services/api';

const AIChatbot = () => {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([
    { role: 'bot', text: "Hey! I'm the KrazyBets AI Assistant. Ask me anything about betting, rules, or how the platform works!" }
  ]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const messagesEndRef = useRef(null);
  const sessionId = useRef(`chat_${Date.now()}`);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleSend = async () => {
    if (!input.trim() || loading) return;
    const userMsg = input.trim();
    setInput('');
    setMessages(prev => [...prev, { role: 'user', text: userMsg }]);
    setLoading(true);

    try {
      const { data } = await api.post('/chat', { message: userMsg, session_id: sessionId.current });
      setMessages(prev => [...prev, { role: 'bot', text: data.response }]);
      if (data.escalated) {
        setMessages(prev => [...prev, { role: 'system', text: 'Your request has been forwarded to our support team. An agent will review it shortly.' }]);
      }
    } catch (e) {
      setMessages(prev => [...prev, { role: 'bot', text: "Sorry, I'm having trouble connecting. Please try again!" }]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {/* Floating Button */}
      {!open && (
        <button
          onClick={() => setOpen(true)}
          className="fixed bottom-20 right-4 md:bottom-6 md:right-6 z-50 w-14 h-14 rounded-full bg-[#D4AF37] text-[#1A1A1A] shadow-lg hover:scale-110 transition-transform flex items-center justify-center"
          data-testid="chatbot-toggle"
        >
          <MessageCircle className="h-6 w-6" />
        </button>
      )}

      {/* Chat Window */}
      {open && (
        <div className="fixed bottom-0 right-0 md:bottom-6 md:right-6 z-50 w-full md:w-96 h-[70vh] md:h-[500px] bg-[#1A1A1A] border border-[#D4AF37]/30 md:rounded-xl shadow-2xl flex flex-col overflow-hidden" data-testid="chatbot-window">
          {/* Header */}
          <div className="bg-[#D4AF37] px-4 py-3 flex items-center justify-between flex-shrink-0">
            <div className="flex items-center gap-2">
              <Bot className="h-5 w-5 text-[#1A1A1A]" />
              <span className="font-bold text-[#1A1A1A]" style={{ fontFamily: 'Outfit' }}>KrazyBets AI</span>
              <span className="w-2 h-2 rounded-full bg-[#28A745]" />
            </div>
            <button onClick={() => setOpen(false)} className="text-[#1A1A1A] hover:opacity-70">
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* Messages */}
          <div className="flex-1 overflow-y-auto p-3 space-y-3">
            {messages.map((msg, i) => (
              <div key={i} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                {msg.role === 'system' ? (
                  <div className="bg-[#28A745]/10 border border-[#28A745]/30 rounded-lg p-2 text-xs text-[#28A745] w-full text-center">
                    {msg.text}
                  </div>
                ) : (
                  <div className={`max-w-[85%] rounded-xl px-3 py-2 text-sm ${
                    msg.role === 'user'
                      ? 'bg-[#D4AF37] text-[#1A1A1A]'
                      : 'bg-[#2D2D2D] text-white'
                  }`}>
                    <div className="flex items-center gap-1 mb-1 opacity-70">
                      {msg.role === 'bot' ? <Bot className="h-3 w-3" /> : <User className="h-3 w-3" />}
                      <span className="text-xs font-semibold">{msg.role === 'bot' ? 'AI Assistant' : 'You'}</span>
                    </div>
                    <div className="whitespace-pre-wrap break-words">{msg.text}</div>
                  </div>
                )}
              </div>
            ))}
            {loading && (
              <div className="flex justify-start">
                <div className="bg-[#2D2D2D] rounded-xl px-4 py-3 text-white">
                  <div className="flex gap-1">
                    <div className="w-2 h-2 bg-[#D4AF37] rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                    <div className="w-2 h-2 bg-[#D4AF37] rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                    <div className="w-2 h-2 bg-[#D4AF37] rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                  </div>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Input */}
          <div className="border-t border-[#3D3D3D] p-3 flex gap-2 flex-shrink-0">
            <input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleSend()}
              placeholder="Ask anything about KrazyBets..."
              className="flex-1 bg-[#2D2D2D] border border-[#3D3D3D] rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-[#D4AF37]"
              data-testid="chatbot-input"
            />
            <button
              onClick={handleSend}
              disabled={loading || !input.trim()}
              className="bg-[#D4AF37] text-[#1A1A1A] rounded-lg px-3 py-2 hover:opacity-90 disabled:opacity-50 transition-opacity"
              data-testid="chatbot-send"
            >
              <Send className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}
    </>
  );
};

export default AIChatbot;
